/**
 * AmmoCounter - HUD ammo block matching the Game UI Kit WeaponCard layout.
 * Big current value, muted "/ max" divider, optional weapon name row, and a
 * red low/empty warning state.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface AmmoCounterOptions extends BaseUIComponentOptions {
  /** Optional weapon name shown above the ammo numbers (uppercase). */
  weaponName?: string;
  /** Ammo count at or below which the warning state activates. */
  lowAmmoThreshold?: number;
  /** Toggle the red low/empty warning color on the current number. */
  showLowAmmoWarning?: boolean;
}

export class AmmoCounter extends BaseUIComponent<AmmoCounterOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Ammo Counter',
    category: 'hud',
    summary: 'Displays current/max ammunition with a low-ammo warning.',
    useCases: ['ammo', 'ammunition', 'magazine', 'bullets', 'reload'],
    optionsType: 'AmmoCounterOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/ammo-counter.html',
      styles: '@engine/assets/ui/styles/ammo-counter.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private weaponElement: HTMLElement | null = null;
  private currentElement: HTMLElement | null = null;
  private dividerElement: HTMLElement | null = null;

  private currentAmmo = 0;
  private maxAmmo = 0;

  protected override getAssetPaths() {
    return {
      templatePath: AmmoCounter.metadata.assetPaths.template,
      stylesPath: AmmoCounter.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<AmmoCounterOptions> {
    return {
      position: 'bottom-right',
      visible: true,
      customClasses: [],
      customStyles: {},
      weaponName: '',
      lowAmmoThreshold: 5,
      showLowAmmoWarning: true,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      currentAmmo: this.currentAmmo,
      maxAmmo: this.maxAmmo,
      weaponName: this.options.weaponName,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-ammo-counter') as HTMLElement | null;
    this.weaponElement = this.layout.querySelector('[data-ammo-weapon]') as HTMLElement | null;
    this.currentElement = this.layout.querySelector('[data-ammo-current]') as HTMLElement | null;
    this.dividerElement = this.layout.querySelector('[data-ammo-divider]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.updateDisplay();
  }

  /** Update the displayed ammo. Pass current and magazine max. */
  public setAmmo(current: number, max: number): void {
    this.currentAmmo = Math.max(0, current);
    this.maxAmmo = Math.max(0, max);
    this.updateDisplay();
  }

  /** Update the weapon label shown above the numbers. */
  public setWeaponName(name: string): void {
    this.options.weaponName = name;
    if (this.weaponElement) this.weaponElement.textContent = name;
  }

  public getCurrentAmmo(): number {
    return this.currentAmmo;
  }

  public getMaxAmmo(): number {
    return this.maxAmmo;
  }

  public isLowAmmo(): boolean {
    return this.currentAmmo <= this.options.lowAmmoThreshold && this.currentAmmo > 0;
  }

  public isEmpty(): boolean {
    return this.currentAmmo === 0;
  }

  private updateDisplay(): void {
    if (this.currentElement) this.currentElement.textContent = String(this.currentAmmo);
    if (this.dividerElement) this.dividerElement.textContent = `/ ${this.maxAmmo}`;

    if (!this.rootElement) return;

    let state: 'normal' | 'low' | 'empty' = 'normal';
    if (this.options.showLowAmmoWarning) {
      if (this.isEmpty()) state = 'empty';
      else if (this.isLowAmmo()) state = 'low';
    }
    this.rootElement.dataset.state = state;
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.weaponElement = null;
    this.currentElement = null;
    this.dividerElement = null;
  }
}
